/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  FormProvider, SubmitHandler, useForm,
} from 'react-hook-form';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button, Grid, IconButton, Card,
} from '@mui/material';
import * as yup from 'yup';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import { FiPlusCircle } from 'react-icons/fi';
import { FaLinkedin, FaGithub, FaFacebook, FaTwitter, FaTelegram } from "react-icons/fa";
import { FaSquareArrowUpRight } from "react-icons/fa6";
import { MdCancel } from "react-icons/md";
import { FcApproval } from "react-icons/fc";
import 'react-circular-progressbar/dist/styles.css';
import { ExpandUpIcon, CloseIcon } from 'src/icons';
import Input from 'src/components/elements/Input';
import RHFInput from 'src/components/elements/Input/RHFInput';
import RHFSelect from 'src/components/elements/Select/RHFSelect';
import { Option } from 'src/components/elements/Select';
import RHFPhoneInput from 'src/components/elements/Input/RHFPhoneInput';
import { DialogContentProps } from 'src/components/Dialog/Dialog';
import { JobType, PayType } from 'src/pages/CandidateInfo/types';
import { CurrencyType } from 'src/pages/CandidateInfo/types';
import { scrollToError } from 'src/utils/FormUtils';
import RHFDOBInput from 'src/components/elements/Input/RHFDOBInput';
import RHFTextArea from 'src/components/elements/TextArea/RHFTextArea';
import RHFAutoComplete from 'src/components/elements/AutoComplete/RHFAutoComplete';
import { useVendors } from 'src/hooks/query/vendors';
import { ContactInfo, Vendor } from 'src/pages/ViewJob/types';
import { AuthProvider } from '@panna/auth';
import AddAndRefreshConsultancyOptions from 'src/pages/ViewJob/components/AddAndRefreshConsultancyOptions';
import Requests from 'src/network/accounts';
import { useLocations } from 'src/hooks/query/locations';
import { useJobTypes } from 'src/hooks/query/job-types';
import { mobileRegExp } from 'src/utils/Regex';

interface PlacedWithMroadsFormProps extends DialogContentProps {
  status: string;
  onSave: (data: any) => void;
  resumeInfo: PlacedWithMroadsFormValues;
  payTypes: PayType[];
  preferredLocations: string[];
  workAuthorizations: string[];
  resumeSources: string[];
  onCancel: () => void;
}

export interface PlacedWithMroadsFormValues {
  firstName: string;
  lastName: string;
  candidateLocation: string;
  preferredJobLocation: string;
  phoneNumber: string;
  candidateTitle: string;
  experienceLevel: string;
  emailAddress: string;
  payType: string;
  payRate: string;
  dateOfBirth: string;
  visaStatus: string;
  reLocation: string;
  resumeSource: string;
  consultancyId: string;
  consultancyContactId: string;
  consultancyPhoneNumber: string;
  extensionNumber: string;
  consultancyEmailAddress: string;
  comments: string;
  socialLinks: string;
}

const relocationOptions = [
  {label: 'YES', value: 'YES'},
  {label: 'NO', value: 'NO'},
]

const experienceOptions = [
  {label: 'Beginner (0-2 years)', value: 'Beginner (0-2 years)'},
  {label: 'Advanced (2-5 years)', value: 'Advanced (2-5 years)'},
  {label: 'Expert(5+ years)', value: 'Expert(5+ years)'},
];

const defaultCollapsibleSections = [{
  name: 'Basic Details',
  expanded: true,
},
{
  name: 'Additional Details',
  expanded: true,
},
{
  name: 'Consultancy Details',
  expanded: true,
}];

const PlacedWithMroadsForm = function PlacedWithMroadsForm(props: PlacedWithMroadsFormProps) {

  const {fetchVendors, vendorOptions, vendorsLoading} = useVendors();

  // const [currency, setCurrency] = useState<CurrencyType>('USD');
  const [socialLinks, setSocialLinks] = useState<string[]>([]);
  const [socialLinkInput, setSocialLinkInput] = useState<string>('');
  const [showSocialLink, setShowSocialLink] = useState<boolean>(false);
  const [collapsibleSections, setCollapsibleSections] = useState(defaultCollapsibleSections);
  const [contactOptions, setContactoptions] = useState<Option<string, string>[]>([]);
  const [consultancyContactInfo, setConsultancyContactInfo] = useState<ContactInfo[]>([]);

  const {
    status,
    onSave,
    resumeInfo,
    payTypes,
    preferredLocations,
    workAuthorizations,
    resumeSources,
    onCancel,
  } = props;

  const payTypesArray: Option<string, string>[] = payTypes?.map((each) => ({
    label: each.name,
    value: (each.id).toString(),
  }));

  const {locationsOptions} = useLocations();
  const {jobTypesOptions} = useJobTypes();

  const sourcesArray: Option<string, string>[] = resumeSources?.map((each) => ({
    label: each,
    value: each,
  }));

  const workAuthorizationsArray: Option<string, string>[] = workAuthorizations?.map((each) => ({
    label: each,
    value: each,
  }));

  const preferredLocsArr: Option<string, string>[] = preferredLocations
    .map(locationId => locationsOptions.find(option => option.value === locationId))
    .filter(option => option !== undefined) as Option<string, string>[];

  const validationSchema = yup.object().shape({
    firstName: yup.string().typeError('Enter valid First name')
    .required('First Name is required'),
    lastName: yup.string().typeError('Enter valid Last name').required('Last Name is required'),
    candidateLocation: yup.string().typeError('Enter valid Location')
    .required('Location is required'),
    candidateTitle: yup.string().typeError('Enter valid Job title')
    .required('Job title is required'),
    preferredJobLocation: yup.string().typeError('Enter valid Preferred Job Location')
    .required('Preferred Job Location is required'),
    phoneNumber: yup
    .string()
    .nullable()
    .required('Contact number is required')
    .test(
      'isValidPhone',
      'Enter valid contact number',
      (value) => !value || value === '' || mobileRegExp.test(value)
    ),
    payType: yup.string().typeError('Enter valid Pay type')
    .required('Pay type is required'),
    payRate: yup.number().typeError('Enter valid pay rate')
    .required('Pay rate is required'),
    resumeSource: yup.string().typeError('Enter valid source'),
    comments: yup.string().typeError('Enter valid notes')
    .required('Notes is required'),
    emailAddress: yup.string().email('Enter valid Email Address').required('Email Address is required'),
    consultancyId: yup
      .string()
      .when(['payType'], (values, schema) => {
        const payTypeSelected = values[0];
        const matchingPayType = payTypesArray.find((payType) => payType.value === payTypeSelected);
        const isC2C = matchingPayType ? matchingPayType.label.toLowerCase().includes('c2c') : false;
        return isC2C
          ? schema.required('Consultancy Name required')
          : schema.notRequired();
      }),
      consultancyContactId: yup
      .string()
      .when(['payType'], (values, schema) => {
        const payTypeSelected = values[0];
        const matchingPayType = payTypesArray.find((payType) => payType.value === payTypeSelected);
        const isC2C = matchingPayType ? matchingPayType.label.toLowerCase().includes('c2c') : false;
        return isC2C
          ? schema.required('Consultancy Contact Person required')
          : schema.notRequired();
      }),
      consultancyPhoneNumber: yup
      .string(),
      extensionNumber: yup
      .string(),
      consultancyEmailAddress: yup
      .string()
      .email(),
  });

  const methods = useForm<PlacedWithMroadsFormValues>({
    defaultValues: {
      candidateTitle: '',
      payType: '',
      payRate: '',
      consultancyId: '',
      consultancyContactId: '',
      consultancyPhoneNumber: '',
      extensionNumber: '',
      consultancyEmailAddress: '',
      firstName: '',
      lastName: '',
      reLocation: '',
      candidateLocation: '',
      preferredJobLocation: '',
      experienceLevel: '',
      emailAddress: '',
      phoneNumber: '',
      dateOfBirth: '',
      visaStatus: '',
      resumeSource: '',
      comments: '',
    },
    resolver: useYupValidationResolver(validationSchema),
  });

  const {
    reset,
    handleSubmit,
    watch,
    setValue,
    formState: {isDirty, errors},
    clearErrors,
  } = methods;

  useEffect(() => {
    reset({
      candidateTitle: resumeInfo?.candidateTitle || '',
      payType: resumeInfo?.payType || '',
      payRate: resumeInfo?.payRate || '',
      consultancyId: resumeInfo?.consultancyId || '',
      consultancyContactId: resumeInfo?.consultancyContactId || '',
      consultancyPhoneNumber: resumeInfo?.consultancyPhoneNumber || '',
      extensionNumber: resumeInfo?.extensionNumber || '',
      consultancyEmailAddress: resumeInfo?.consultancyEmailAddress || '',
      firstName: resumeInfo?.firstName || '',
      lastName: resumeInfo?.lastName || '',
      reLocation: resumeInfo?.reLocation || '',
      candidateLocation: resumeInfo?.candidateLocation || '',
      preferredJobLocation: resumeInfo?.preferredJobLocation || '',
      experienceLevel: resumeInfo?.experienceLevel || '',
      emailAddress: resumeInfo?.emailAddress || '',
      phoneNumber: resumeInfo?.phoneNumber || '',
      dateOfBirth: resumeInfo?.dateOfBirth || '',
      visaStatus: resumeInfo?.visaStatus || '',
      resumeSource: resumeInfo?.resumeSource || '',
      comments: resumeInfo?.comments || '',
    });
     if (resumeInfo?.socialLinks && resumeInfo?.socialLinks !== '') {
       const links = resumeInfo?.socialLinks.split(',');
       setSocialLinks(links);
     }
  }, [resumeInfo]);

  useEffect(() => {
    if (!vendorOptions?.length) fetchVendors();
  }, []);

  const submitHandler: SubmitHandler<PlacedWithMroadsFormValues> = (data: PlacedWithMroadsFormValues) => {
    onSave(data);
  };

  const getConsultancyDetails = async () => {
    try {
      const consultancyId = watch("consultancyId");
      if (!consultancyId) {
        setValue('consultancyContactId', '');
        setValue("consultancyPhoneNumber", "");
        setValue("extensionNumber", "");
        setValue("consultancyEmailAddress", "");
        setContactoptions([]);
        return;
      }
      const res = await Requests.getInstance().getConsultancyById(consultancyId);
      const contactInfo = res.data.contactInfo;
      const contactOptions = contactInfo?.map((item: ContactInfo) => ({
        label: `${item.firstName || ''} ${item.lastName || ''}`.trim(),
        value: item.id?.toString() || '0',
      })) || [];
      setConsultancyContactInfo(contactInfo || []);
      setContactoptions(contactOptions);
      if(isDirty){
        if (contactOptions.length === 1) {
          setValue('consultancyContactId', contactOptions[0].value);
          clearErrors('consultancyContactId');
        } else {
          setValue('consultancyContactId', '');
        }
      }
    } catch (e) {
      console.info('error occured while fetching consultancyById', e);
    }
  }

  const addSocialLink = () => {
    if (socialLinkInput.includes('.com')) {
      const formattedInput = socialLinkInput.toLowerCase().replace('www.', '');
      const linkInput = formattedInput.replace('.com', '');
      if (!resumeInfo?.socialLinks?.includes(linkInput)) {
        const updatedSocialLinks = [...socialLinks];
        updatedSocialLinks.push(socialLinkInput.toLowerCase());
        setSocialLinks(updatedSocialLinks);
        setSocialLinkInput('');
        setShowSocialLink(false);
      }
    }
  }

  const handleAccordionChange = (name: string) => {
    const updatedData = collapsibleSections.map(each => {
      if (each.name === name) {
        return {
          ...each,
          expanded: !each.expanded,
        };
      }
      return each;
    });
    setCollapsibleSections(updatedData);
  };

  const openSocialLink = (link: string) => {
    if (!link.includes('http')) {
      window.open('http://'+link, '_blank');
    } else {
      window.open(link, '_blank');
    }
  }

  const getIcon = (link: string) => {
    if (link?.toLowerCase().includes('linkedin.com')) {
      return <FaLinkedin color='blue' />
    } else if (link?.toLowerCase().includes('github.com')) {
      return <FaGithub color='purple' />
    } else if (link?.toLowerCase().includes('facebook.com')) {
      return <FaFacebook color='blue' />
    } else if (link?.toLowerCase().includes('telegram.com')) {
      return <FaTelegram color='blue' />
    } else if (link?.toLowerCase().includes('twitter.com')) {
      return <FaTwitter color='blue' />
    } else {
      return <FaSquareArrowUpRight />
    }
  }

  const onError = () => {
    scrollToError('placed-with-form');
  };

  useEffect(() => {
    getConsultancyDetails();
  }, [watch("consultancyId")]);

  useEffect(() => {
      const contactPersonNameId = watch("consultancyContactId");
      const contactPerson = consultancyContactInfo?.find((item) => item?.id?.toString() === contactPersonNameId);
      setValue("consultancyPhoneNumber", contactPerson?.phoneNumber || "");
      setValue("extensionNumber", contactPerson?.extensionNumber || "");
      setValue("consultancyEmailAddress", contactPerson?.emailAddress || "");
  }, [watch("consultancyContactId"), contactOptions]);

  return (
    <FormProvider {...methods}>
      <form id="placed-with-form" onSubmit={handleSubmit(submitHandler, onError)} className="bg-white p-10 flex gap-8 justify-center items-center flex-col">
        <div style={{ marginBottom: 8 }}>
          <div className="mt-2 mb-1 text-center">Candidate Details</div>
          <div className="mt-1 mb-8 text-center text-sm">(Fill all the mandatory fields to proceed)</div>
          {collapsibleSections.map(each => (
            <Accordion
            expanded={each.expanded}
            onChange={() => handleAccordionChange(each.name)}
            sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandUpIcon />}
                aria-controls="content"
                id="content"
              >
                <Typography>{each.name}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ mb: 1 }}>
                <>
                  {each.name === 'Basic Details' ? (
                    <div>
                      <Grid container spacing={3} mb={4} pl={3}>
                        <Grid item xs={12} sm={12} md={6} lg={4}>
                          <RHFInput control={methods.control} name="firstName" label="First Name*" />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={4}>
                          <RHFInput control={methods.control} name="lastName" label="Last Name*" />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={4}>
                          <div className="h-full flex items-end">
                            <RHFAutoComplete
                              control={methods.control}
                              name="candidateLocation"
                              label="Location*"
                              options={locationsOptions}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={4}>
                          <div className="h-full flex items-end">
                            <RHFSelect
                              control={methods.control}
                              name="candidateTitle"
                              label="Job Title*"
                              options={jobTypesOptions}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={4}>
                          <div className="h-full flex items-end">
                            <RHFSelect
                              control={methods.control}
                              name="experienceLevel"
                              label="Experience"
                              options={experienceOptions}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={4}>
                          <div className="h-full flex items-end">
                            <RHFSelect
                              control={methods.control}
                              name="preferredJobLocation"
                              label="Preferred Job Location*"
                              options={preferredLocsArr}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={4}>
                          <RHFPhoneInput
                            control={methods.control}
                            name="phoneNumber"
                            label="Contact Number*"
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={4}>
                          <RHFInput
                            control={methods.control}
                            name="emailAddress"
                            label="Email Address"
                            disabled
                          />
                        </Grid>
                      </Grid>
                      {socialLinks && socialLinks.length > 0 && socialLinks.map((each) => {
                        return (
                          <IconButton onClick={() => openSocialLink(each)}>
                            {getIcon(each)}
                          </IconButton>
                        )
                      })}
                      <div className="pl-5 mt-2 pt-0 mb-2">
                        <Button
                          variant="text"
                          startIcon={<FiPlusCircle />}
                          onClick={() => setShowSocialLink(true)}
                        >
                          Add Social Link
                        </Button>
                      </div>
                      {showSocialLink && (
                        <Grid container spacing={3} mb={4} pl={3}>
                          <Grid item xs={12} sm={12} md={6} lg={4}>
                            <Input
                              value={socialLinkInput}
                              onChange={(event: any) => {
                                setSocialLinkInput(event)
                              }}
                            />
                          </Grid>
                          <Grid display="flex" sx={{ mt: 3 }}>
                            <IconButton
                              onClick={addSocialLink}
                            >
                              <FcApproval color='#F97171' />
                            </IconButton>
                            <IconButton 
                              onClick={() => {
                                setShowSocialLink(false);
                                setSocialLinkInput('');
                              }}
                            >
                              <MdCancel color='red' />
                            </IconButton>
                          </Grid>
                        </Grid>
                      )}
                    </div>
                  ) :  each.name === 'Additional Details' ? (
                    <Grid container spacing={3} mb={4} pl={3}>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <div className="h-full flex items-start">
                          <RHFSelect
                            control={methods.control}
                            name="payType"
                            label="Pay Type*"
                            options={payTypesArray}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <RHFInput
                          control={methods.control}
                          name="payRate"
                          label="Pay Rate*"
                            //  iconStart="$"
                            //  currencyName={'USD'}
                            //  onCurrencyChange={(e) => setCurrency(e.currencyName as CurrencyType)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <RHFDOBInput control={methods.control} name="dateOfBirth" label="D.O.B (mm/dd)" />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <div className="h-full flex items-end">
                          <RHFSelect
                            control={methods.control}
                            name="visaStatus"
                            label="Work Authorization"
                            options={workAuthorizationsArray}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <div className="h-full flex items-end">
                          <RHFSelect
                            control={methods.control}
                            name="reLocation"
                            label="Relocation"
                            options={relocationOptions}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <div className="h-full flex items-start pt-7">
                          <RHFSelect
                            control={methods.control}
                            name="resumeSource"
                            label="Source"
                            options={sourcesArray}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  ) : each.name === 'Consultancy Details' ? (
                    <Grid container spacing={3} mb={4} pl={3}>
                      <Grid item xs={12} sm={12} md={12} lg={8}>
                        <div className="h-full relative -bottom-[25px] flex items-start mb-8">
                          <RHFAutoComplete control={methods.control} name="consultancyId" label="Consultancy Name" options={vendorOptions} />
                          <AuthProvider>
                            <AddAndRefreshConsultancyOptions fetchVendors={fetchVendors} />
                          </AuthProvider>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <div className="h-full relative -bottom-[25px]">
                          <RHFAutoComplete control={methods.control} name="consultancyContactId" label="Contact Person" options={contactOptions} />
                          </div>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <RHFPhoneInput
                          control={methods.control}
                          name="consultancyPhoneNumber"
                          label="Contact Number"
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <RHFInput
                          control={methods.control}
                          name="extensionNumber"
                          label="Ext No."
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <RHFInput control={methods.control} name="consultancyEmailAddress" label="Email Address" disabled />
                      </Grid>
                    </Grid>
                  ) : null}
                </>
              </AccordionDetails>
            </Accordion>
          ))}
          <div className='mt-2 pl-2 pr-2'>
            <RHFTextArea
              name="comments"
              label="Notes*"
              control={methods.control}
              rows={3}
            />
          </div>
        </div>
        <div className="w-full flex  gap-2 ">
          <Button
            variant="contained"
            size="large"
            type="submit"
            sx={{ marginTop: '10px', marginBottom: '15px', textTransform: 'capitalize' }}
          >
            {status.toLowerCase()}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={onCancel}
            sx={{ marginTop: '10px', marginBottom: '15px'}}
          >
            Cancel
          </Button>
        </div>
        <div
          className="absolute right-2 top-2"
          onClick={onCancel}
        >
          <CloseIcon className="cursor-pointer" />
        </div>
      </form>
    </FormProvider>
  );
};

export default PlacedWithMroadsForm;
